import { SlidePlugin } from './SlidePlugin.interface';
import { PluginValidator } from './PluginValidator';
import { SlideCapabilities } from '../types';

/**
 * Registry for slide plugins
 * Manages registration, validation, and discovery of slide plugins
 */
export class SlideRegistry {
  private plugins: Map<string, SlidePlugin> = new Map();
  private validators: PluginValidator[] = [];
  private initialized = false;

  /**
   * Register a new plugin
   */
  register(plugin: SlidePlugin): void {
    // Validate plugin structure
    this.validatePlugin(plugin);

    // Check for conflicts
    if (this.plugins.has(plugin.type)) {
      throw new Error(
        `Plugin type "${plugin.type}" is already registered. Cannot register duplicate plugins.`
      );
    }

    // Register the plugin
    this.plugins.set(plugin.type, plugin);

    console.log(`✅ Registered slide plugin: ${plugin.type} (${plugin.displayName}) v${plugin.version}`);
  }

  /**
   * Register multiple plugins at once
   */
  registerMany(plugins: SlidePlugin[]): void {
    plugins.forEach((plugin) => this.register(plugin));
  }

  /**
   * Unregister a plugin
   */
  unregister(type: string): boolean {
    const deleted = this.plugins.delete(type);
    if (deleted) {
      console.log(`❌ Unregistered slide plugin: ${type}`);
    }
    return deleted;
  }

  /**
   * Get a plugin by type
   */
  get(type: string): SlidePlugin | undefined {
    return this.plugins.get(type);
  }

  /**
   * Get a plugin by type (throws if not found)
   */
  getOrThrow(type: string): SlidePlugin {
    const plugin = this.get(type);
    if (!plugin) {
      throw new Error(`Plugin type "${type}" is not registered`);
    }
    return plugin;
  }

  /**
   * Check if a plugin is registered
   */
  has(type: string): boolean {
    return this.plugins.has(type);
  }

  /**
   * Get all registered plugins
   */
  getAll(): SlidePlugin[] {
    return Array.from(this.plugins.values());
  }

  /**
   * Get all plugin types
   */
  getTypes(): string[] {
    return Array.from(this.plugins.keys());
  }

  /**
   * Get plugins by capability
   */
  getByCapability(capability: keyof SlideCapabilities): SlidePlugin[] {
    return this.getAll().filter((plugin) => plugin.capabilities[capability]);
  }

  /**
   * Get plugins that support scoring
   */
  getScoreable(): SlidePlugin[] {
    return this.getAll().filter((plugin) => plugin.capabilities.isScoreable);
  }

  /**
   * Get plugins that require upload
   */
  getUploadable(): SlidePlugin[] {
    return this.getAll().filter((plugin) => plugin.capabilities.requiresUpload);
  }

  /**
   * Add a custom validator
   */
  addValidator(validator: PluginValidator): void {
    this.validators.push(validator);
  }

  /**
   * Clear all plugins
   */
  clear(): void {
    this.plugins.clear();
    console.log('🗑️ Cleared all slide plugins');
  }

  /**
   * Get registry statistics
   */
  getStats(): {
    totalPlugins: number;
    pluginTypes: string[];
    scoreable: number;
    uploadable: number;
  } {
    const allPlugins = this.getAll();
    return {
      totalPlugins: allPlugins.length,
      pluginTypes: this.getTypes(),
      scoreable: this.getScoreable().length,
      uploadable: this.getUploadable().length,
    };
  }

  /**
   * Initialize the registry (call once on app startup)
   */
  initialize(): void {
    if (this.initialized) {
      console.warn('SlideRegistry is already initialized');
      return;
    }

    this.initialized = true;
    console.log('📚 SlideRegistry initialized');
    console.log('📊 Registry stats:', this.getStats());
  }

  /**
   * Check if registry is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Validate a plugin
   */
  private validatePlugin(plugin: SlidePlugin): void {
    // Basic structure validation
    if (!plugin.type) {
      throw new Error('Plugin must have a type');
    }

    if (!plugin.displayName) {
      throw new Error(`Plugin ${plugin.type} must have a displayName`);
    }

    if (!plugin.version) {
      throw new Error(`Plugin ${plugin.type} must have a version`);
    }

    // Component validation
    if (!plugin.CreationComponent) {
      throw new Error(`Plugin ${plugin.type} must have a CreationComponent`);
    }

    if (!plugin.CompletionComponent) {
      throw new Error(`Plugin ${plugin.type} must have a CompletionComponent`);
    }

    if (!plugin.PreviewComponent) {
      throw new Error(`Plugin ${plugin.type} must have a PreviewComponent`);
    }

    if (!plugin.ReportComponent) {
      throw new Error(`Plugin ${plugin.type} must have a ReportComponent`);
    }

    // Method validation
    if (typeof plugin.createDefault !== 'function') {
      throw new Error(`Plugin ${plugin.type} must have a createDefault method`);
    }

    if (typeof plugin.duplicate !== 'function') {
      throw new Error(`Plugin ${plugin.type} must have a duplicate method`);
    }

    if (typeof plugin.validateCreation !== 'function') {
      throw new Error(`Plugin ${plugin.type} must have a validateCreation method`);
    }

    if (typeof plugin.validateAnswer !== 'function') {
      throw new Error(`Plugin ${plugin.type} must have a validateAnswer method`);
    }

    // Capabilities validation
    if (!plugin.capabilities || typeof plugin.capabilities !== 'object') {
      throw new Error(`Plugin ${plugin.type} must have capabilities object`);
    }

    // Run custom validators
    this.validators.forEach((validator) => {
      validator.validate(plugin);
    });
  }
}

// Create global registry instance
export const slideRegistry = new SlideRegistry();

// Export for convenience
export default slideRegistry;